import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Alert,
  Platform,
} from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { ParentStackParamList } from "../../navigation/types";
import { authApi } from "../../api/auth";
import { useAuthStore } from "../../state/authStore";
import AuthShell, { AuthField, authStyles } from "../../components/AuthShell";
import { colors } from "../../theme";

type Props = NativeStackScreenProps<ParentStackParamList, "Login">;

export default function LoginScreen({ navigation }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState<string | null>(null);
  const { setTokens } = useAuthStore();

  const goHome = () =>
    (navigation as any).replace(
      Platform.OS === "web" ? "WebShell" : "ProfileSelect",
    );

  const handleGoogleToken = async (idToken: string) => {
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.google(idToken);
      setTokens(tokens.accessToken, tokens.refreshToken, tokens.parentId);
      goHome();
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Google sign-in failed. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    setError(null);
    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const tokens = await authApi.login(email, password);
      setTokens(tokens.accessToken, tokens.refreshToken, tokens.parentId);
      // Web is the parent dashboard; mobile is kid-first (drop into the
      // profile picker — parent mode is PIN-gated behind the lock badge).
      goHome();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      mode="signin"
      eyebrow="Welcome back"
      title="Sign in to Kidly"
      subtitle="Pick up where your kids left off — videos, stickers, and weekly recaps."
      onSwitchMode={(m) => {
        if (m === "signup") navigation.navigate("Signup");
      }}
      onGoogleToken={handleGoogleToken}
      footer={
        // Browse-first on every platform: a signed-out visitor can always get
        // back out to the free library instead of being stuck on an auth page.
        // Web has its own shell route; native has the guest home.
        <TouchableOpacity
          accessibilityRole="link"
          onPress={() =>
            (navigation as any).navigate(
              Platform.OS === "web" ? "WebShell" : "GuestHome",
            )
          }
        >
          <Text style={authStyles.link}>← Browse free videos, no account needed</Text>
        </TouchableOpacity>
      }
    >
      <AuthField label="Email">
        <TextInput
          style={[
            authStyles.input,
            focused === "email" && authStyles.inputFocused,
          ]}
          placeholder="you@home.com"
          placeholderTextColor={colors.ink3}
          autoCapitalize="none"
          keyboardType="email-address"
          value={email}
          onChangeText={setEmail}
          onFocus={() => setFocused("email")}
          onBlur={() => setFocused(null)}
        />
      </AuthField>

      <AuthField label="Password">
        <TextInput
          style={[
            authStyles.input,
            focused === "password" && authStyles.inputFocused,
          ]}
          placeholder="Your password"
          placeholderTextColor={colors.ink3}
          secureTextEntry
          value={password}
          onChangeText={setPassword}
          onFocus={() => setFocused("password")}
          onBlur={() => setFocused(null)}
          onSubmitEditing={handleLogin}
        />
      </AuthField>

      <TouchableOpacity
        accessibilityRole="link"
        style={{ alignSelf: "flex-end", marginTop: -4, marginBottom: 4 }}
        onPress={() => navigation.navigate("ForgotPassword")}
      >
        <Text style={authStyles.link}>Forgot password?</Text>
      </TouchableOpacity>

      {error ? <Text style={authStyles.error}>{error}</Text> : null}

      <TouchableOpacity
        accessibilityRole="button"
        style={[authStyles.submit, loading && authStyles.submitDisabled]}
        onPress={handleLogin}
        disabled={loading}
        activeOpacity={0.85}
      >
        <Text style={authStyles.submitText}>
          {loading ? "Signing in…" : "Sign in"}
        </Text>
      </TouchableOpacity>

      <View style={authStyles.meta}>
        <Text style={authStyles.metaText}>New to Kidly?</Text>
        <TouchableOpacity
          accessibilityRole="link"
          onPress={() => navigation.navigate("Signup")}
        >
          <Text style={authStyles.link}>Create an account</Text>
        </TouchableOpacity>
      </View>
    </AuthShell>
  );
}
